// =====================================================================
// KinsCircle Phase 13 — Graph Mutation Audit Log
// =====================================================================
//
// Append-only ring buffer that records every mutation attempt routed
// through `graphMutationGateway`. Pure client-side memory + best-effort
// localStorage persistence (last 200 entries) so admins can inspect the
// recent history without a server round-trip.
//
// Each entry records:
//   • acting user id            (auth.users.id, may be null)
//   • mutation type             ('insert-member' / 'update-member' /
//                                'delete-member' / 'set-spouse' /
//                                'set-parents' / 'claim-member' /
//                                'release-claim')
//   • target row id (when known)
//   • before-state snapshot     (subset of columns, PII-minimal)
//   • after-state snapshot
//   • validation result         ('allowed' / 'blocked' / 'failed')
//   • blocker code              (when blocked, e.g. 'circular-ancestry')
//   • timestamp                 (ISO-8601)
//
// PII-minimal — we deliberately do NOT log email addresses, full bios,
// memory text, photo URLs, or storage paths. We log ids, names, and the
// shape of the change.
//
// PURE infrastructure — never throws into the caller. If localStorage is
// unavailable (private browsing, quota exceeded, SSR), we silently fall
// back to in-memory only.

const STORAGE_KEY = 'kc:graph-mutation-audit:v1';
const RING_CAPACITY = 200;

export type GraphMutationKind =
  | 'insert-member'
  | 'update-member'
  | 'delete-member'
  | 'restore-member'
  | 'set-spouse'
  | 'set-parents'
  | 'claim-member'
  | 'release-claim';

export type GraphMutationOutcome = 'allowed' | 'blocked' | 'failed';

export type ConstitutionVerdict = 'PASS' | 'WARNING' | 'BLOCK';

export interface GraphMutationAuditEntry {
  /** Stable UUID-ish id for this audit entry. */
  id: string;
  /** Governor-level trace id for Constitution decision review. */
  governorDecisionId: string;
  timestamp: string;
  /** Auth user id of the actor (may be null for system-driven ops). */
  actingUserId: string | null;
  kind: GraphMutationKind;
  /** Target row id when known (insert-member starts null and gets the new id on success). */
  targetId: string | null;
  /** Compact before-state snapshot. */
  before: Record<string, unknown> | null;
  /** Compact after-state snapshot. */
  after: Record<string, unknown> | null;
  outcome: GraphMutationOutcome;
  /** Constitution verdict mapped from mutation outcome. */
  constitutionVerdict: ConstitutionVerdict;
  /** Constitution rule or policy most relevant to this mutation. */
  constitutionRuleApplied: string | null;
  /** Human-readable evidence trail for Governor review. */
  constitutionEvidence: string[];
  /** When outcome === 'blocked' or 'failed', a stable machine-readable reason code. */
  blocker: string | null;
  /** Free-form human reason — short. */
  reason: string | null;
}

const ring: GraphMutationAuditEntry[] = [];

function genId(): string {
  // Lightweight non-cryptographic id — sufficient for audit dedupe.
  return (
    Date.now().toString(36) +
    '-' +
    Math.random().toString(36).slice(2, 10)
  );
}


function makeGovernorDecisionId(kind: GraphMutationKind): string {
  return `GOV-${kind}-${new Date().toISOString().replace(/[-:.TZ]/g, '').slice(0, 14)}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
}

function inferConstitutionVerdict(outcome: GraphMutationOutcome): ConstitutionVerdict {
  if (outcome === 'allowed') return 'PASS';
  if (outcome === 'blocked') return 'BLOCK';
  return 'WARNING';
}

function inferConstitutionRuleApplied(partial: Omit<GraphMutationAuditEntry, 'id' | 'timestamp' | 'governorDecisionId' | 'constitutionVerdict' | 'constitutionRuleApplied' | 'constitutionEvidence'>): string | null {
  const text = `${partial.kind ?? ''} ${partial.blocker ?? ''} ${partial.reason ?? ''}`.toLowerCase();

  if (text.includes('co-parent') || text.includes('coparent')) return 'coparent-policy';
  if (text.includes('spouse') || text.includes('partner')) return 'spouse-policy';
  if (text.includes('duplicate')) return 'tree-no-duplicates';
  if (text.includes('orphan')) return 'tree-no-orphans';
  if (text.includes('parent')) return 'parent-child-integrity';
  if (text.includes('claim')) return 'member-claim-integrity';
  if (text.includes('delete')) return 'member-deletion-integrity';

  return 'kinscircle-constitution';
}

function buildConstitutionEvidence(
  partial: Omit<GraphMutationAuditEntry, 'id' | 'timestamp' | 'governorDecisionId' | 'constitutionVerdict' | 'constitutionRuleApplied' | 'constitutionEvidence'>,
  verdict: ConstitutionVerdict,
  ruleApplied: string | null
): string[] {
  const evidence = [
    `Constitution ${verdict}`,
    `Rule applied: ${ruleApplied ?? 'none'}`,
    `Mutation kind: ${partial.kind}`,
    `Outcome: ${partial.outcome}`,
  ];

  if (partial.blocker) evidence.push(`Blocker: ${partial.blocker}`);
  if (partial.reason) evidence.push(`Reason: ${partial.reason}`);
  if (partial.targetId) evidence.push(`Target: ${partial.targetId}`);
  if (partial.actingUserId) evidence.push('Acting user present');

  return evidence;
}

function loadFromStorage() {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      for (const e of parsed) {
        if (e && typeof e === 'object' && typeof e.id === 'string') {
          ring.push(e as GraphMutationAuditEntry);
        }
      }
    }
  } catch {
    /* non-fatal */
  }
}

function persist() {
  if (typeof window === 'undefined') return;
  try {
    const tail = ring.slice(-RING_CAPACITY);
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(tail));
  } catch {
    /* non-fatal — quota exceeded etc. */
  }
}

let loaded = false;
function ensureLoaded() {
  if (loaded) return;
  loaded = true;
  loadFromStorage();
}

/**
 * Append one audit entry. Returns the persisted entry (with id +
 * timestamp filled in). NEVER throws.
 */
export function recordGraphMutation(
  partial: Omit<GraphMutationAuditEntry, 'id' | 'timestamp'>,
): GraphMutationAuditEntry {
  ensureLoaded();
  const constitutionVerdict = inferConstitutionVerdict(partial.outcome);
  const constitutionRuleApplied = inferConstitutionRuleApplied(partial);
  const constitutionEvidence = buildConstitutionEvidence(partial, constitutionVerdict, constitutionRuleApplied);

  const entry: GraphMutationAuditEntry = {
    id: genId(),
    timestamp: new Date().toISOString(),
    governorDecisionId: makeGovernorDecisionId(partial.kind),
    constitutionVerdict,
    constitutionRuleApplied,
    constitutionEvidence,
    ...partial,
  };
  ring.push(entry);
  if (ring.length > RING_CAPACITY) {
    ring.splice(0, ring.length - RING_CAPACITY);
  }
  persist();
  // Best-effort console signal at debug level so a failed audit shows up
  // in browser devtools without spamming production users.
  try {
    if (entry.outcome === 'blocked' || entry.outcome === 'failed') {
      // eslint-disable-next-line no-console
      console.warn('[GraphMutationAudit]', entry.kind, entry.outcome, {
        blocker: entry.blocker,
        reason: entry.reason,
        target: entry.targetId,
      });
    } else {
      // eslint-disable-next-line no-console
      console.debug('[GraphMutationAudit]', entry.kind, 'allowed', {
        target: entry.targetId,
      });
    }
  } catch {
    /* non-fatal */
  }
  return entry;
}

/** Read the most recent N entries (newest first). */
export function readGraphMutationAudit(limit = 50): GraphMutationAuditEntry[] {
  ensureLoaded();
  const n = Math.max(1, Math.min(RING_CAPACITY, limit | 0));
  return ring.slice(-n).reverse();
}

/** Aggregate counters for the System Health enforcement card. */
export interface GraphMutationAuditSummary {
  total: number;
  allowed: number;
  blocked: number;
  failed: number;
  byKind: Record<GraphMutationKind, number>;
  topBlockers: Array<{ blocker: string; count: number }>;
  lastAt: string | null;
}

const KINDS: GraphMutationKind[] = [
  'insert-member',
  'update-member',
  'delete-member',
  'restore-member',
  'set-spouse',
  'set-parents',
  'claim-member',
  'release-claim',
];

export function summarizeGraphMutationAudit(): GraphMutationAuditSummary {
  ensureLoaded();
  const byKind: Record<GraphMutationKind, number> = {
    'insert-member': 0,
    'update-member': 0,
    'delete-member': 0,
    'restore-member': 0,
    'set-spouse': 0,
    'set-parents': 0,
    'claim-member': 0,
    'release-claim': 0,
  };
  let allowed = 0;
  let blocked = 0;
  let failed = 0;
  const blockerCounts = new Map<string, number>();
  let lastAt: string | null = null;
  for (const e of ring) {
    if (KINDS.includes(e.kind)) byKind[e.kind] += 1;
    if (e.outcome === 'allowed') allowed += 1;
    else if (e.outcome === 'blocked') blocked += 1;
    else if (e.outcome === 'failed') failed += 1;
    if (e.blocker) {
      blockerCounts.set(e.blocker, (blockerCounts.get(e.blocker) ?? 0) + 1);
    }
    if (!lastAt || e.timestamp > lastAt) lastAt = e.timestamp;
  }
  const topBlockers = Array.from(blockerCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([blocker, count]) => ({ blocker, count }));
  return {
    total: ring.length,
    allowed,
    blocked,
    failed,
    byKind,
    topBlockers,
    lastAt,
  };
}

/** Test-only helper — clears the ring buffer and storage. */
export function _resetGraphMutationAudit(): void {
  ring.length = 0;
  loaded = true;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage?.removeItem(STORAGE_KEY);
    } catch {
      /* non-fatal */
    }
  }
}


export const TREE_GOVERNOR_EVENT_STREAM_V1 = true;

export function emitTreeGovernorEvent(kind: string, payload: any = {}) {
  try {
    recordGraphMutation({
      kind,
      outcome: 'allowed',
      constitutionVerdict: 'PASS',
      timestamp: new Date().toISOString(),
      evidence: payload,
    });
  } catch (e) {
    console.warn("Tree Governor event emit failed:", e);
  }
}
